import type { ThemeId, ThemeOverrides } from '@mendikot/protocol';
import { cssVarName, ROYAL_PALETTE, type Palette, type PaletteToken } from './palette';

export type CardBackStyle =
  | 'royal'
  | 'diya'
  | 'rangoli'
  | 'gulal'
  | 'bandhani'
  | 'tricolor'
  | 'marigold'
  | 'pookalam'
  | 'kite'
  | 'rakhi'
  | 'alpana'
  | 'crescent'
  | 'snowflake';

export type AmbientKind =
  'none' | 'sparks' | 'diyas' | 'gulal' | 'petals' | 'kites' | 'lanterns' | 'snow' | 'confetti';
export type CelebrationKind = 'confetti' | 'fireworks' | 'colors' | 'petals' | 'tricolor' | 'snow';

export interface FestivalTheme {
  id: ThemeId;
  /** i18n key of the theme's display name. */
  nameKey: string;
  /** i18n key of the greeting shown on the home banner. */
  greetingKey: string;
  palette: Palette;
  cardBack: CardBackStyle;
  ambient: { kind: AmbientKind; intensity: number };
  celebration: CelebrationKind;
  /** Emoji-free glyph drawn on the table centre (rendered as SVG on web). */
  emblem: 'chakra' | 'diya' | 'lotus' | 'mandala' | 'kite' | 'star' | 'tree' | 'leaf';
}

type ThemeSpec = Omit<FestivalTheme, 'palette'> & { palette: Partial<Palette> };

const SPECS: Record<ThemeId, ThemeSpec> = {
  default: {
    id: 'default',
    nameKey: 'theme.default',
    greetingKey: 'greeting.default',
    palette: {},
    cardBack: 'royal',
    ambient: { kind: 'none', intensity: 0 },
    celebration: 'confetti',
    emblem: 'chakra',
  },
  diwali: {
    id: 'diwali',
    nameKey: 'theme.diwali',
    greetingKey: 'greeting.diwali',
    palette: {
      bg: '#0d0718',
      bgGlow: '#3a1a4d',
      surface: '#170d26',
      surfaceRaised: '#21133a',
      surfaceMuted: '#120a1f',
      felt: '#3b1d63',
      feltDeep: '#22103d',
      feltLight: '#5a2f8f',
      feltLine: 'rgba(255, 196, 92, 0.2)',
      rim: '#4a210c',
      rimHighlight: '#ffb627',
      accent: '#ffb627',
      accentStrong: '#ffd166',
      accentSoft: 'rgba(255, 182, 39, 0.18)',
      cardBack: '#8a1c3c',
      cardBackPattern: '#ffb627',
    },
    cardBack: 'diya',
    ambient: { kind: 'diyas', intensity: 0.8 },
    celebration: 'fireworks',
    emblem: 'diya',
  },
  holi: {
    id: 'holi',
    nameKey: 'theme.holi',
    greetingKey: 'greeting.holi',
    palette: {
      bg: '#071521',
      bgGlow: '#3d1240',
      surface: '#0d1f2e',
      surfaceRaised: '#14304a',
      felt: '#0f4c6b',
      feltDeep: '#08304a',
      feltLight: '#1a6e94',
      feltLine: 'rgba(255, 120, 200, 0.22)',
      accent: '#ff5fa2',
      accentStrong: '#ff8cc0',
      accentText: '#2a0716',
      accentSoft: 'rgba(255, 95, 162, 0.18)',
      cardBack: '#c2185b',
      cardBackPattern: '#ffd23f',
    },
    cardBack: 'gulal',
    ambient: { kind: 'gulal', intensity: 0.9 },
    celebration: 'colors',
    emblem: 'mandala',
  },
  navratri: {
    id: 'navratri',
    nameKey: 'theme.navratri',
    greetingKey: 'greeting.navratri',
    palette: {
      bg: '#14060a',
      bgGlow: '#4a0f22',
      surface: '#1f0a10',
      surfaceRaised: '#2d0f18',
      felt: '#7a1430',
      feltDeep: '#4d0b1e',
      feltLight: '#9c2346',
      feltLine: 'rgba(255, 170, 60, 0.22)',
      accent: '#ff9f1c',
      accentStrong: '#ffbf5e',
      accentSoft: 'rgba(255, 159, 28, 0.18)',
      cardBack: '#1b4965',
      cardBackPattern: '#ff9f1c',
    },
    cardBack: 'bandhani',
    ambient: { kind: 'sparks', intensity: 0.6 },
    celebration: 'confetti',
    emblem: 'mandala',
  },
  durga_puja: {
    id: 'durga_puja',
    nameKey: 'theme.durga_puja',
    greetingKey: 'greeting.durga_puja',
    palette: {
      bg: '#140507',
      bgGlow: '#511016',
      surface: '#200a0d',
      felt: '#8c1a1f',
      feltDeep: '#5c0f13',
      feltLight: '#ab2a2f',
      accent: '#f2c14e',
      accentStrong: '#f8d77f',
      cardBack: '#f5efe0',
      cardBackPattern: '#b3151b',
    },
    cardBack: 'alpana',
    ambient: { kind: 'petals', intensity: 0.5 },
    celebration: 'petals',
    emblem: 'lotus',
  },
  ganesh_chaturthi: {
    id: 'ganesh_chaturthi',
    nameKey: 'theme.ganesh_chaturthi',
    greetingKey: 'greeting.ganesh_chaturthi',
    palette: {
      bg: '#140a04',
      bgGlow: '#4d2408',
      surface: '#1e1008',
      felt: '#8a3b0b',
      feltDeep: '#5e2807',
      feltLight: '#a95214',
      accent: '#ffb000',
      accentStrong: '#ffcb4d',
      cardBack: '#b3261e',
      cardBackPattern: '#ffd166',
    },
    cardBack: 'marigold',
    ambient: { kind: 'petals', intensity: 0.7 },
    celebration: 'petals',
    emblem: 'lotus',
  },
  onam: {
    id: 'onam',
    nameKey: 'theme.onam',
    greetingKey: 'greeting.onam',
    palette: {
      bg: '#06120a',
      bgGlow: '#1d3d12',
      surface: '#0c1d10',
      felt: '#2f6b1f',
      feltDeep: '#1d4a12',
      feltLight: '#418a2c',
      accent: '#f5c518',
      accentStrong: '#ffd84d',
      cardBack: '#f6f1e3',
      cardBackPattern: '#d1661a',
    },
    cardBack: 'pookalam',
    ambient: { kind: 'petals', intensity: 0.6 },
    celebration: 'petals',
    emblem: 'leaf',
  },
  pongal_sankranti: {
    id: 'pongal_sankranti',
    nameKey: 'theme.pongal_sankranti',
    greetingKey: 'greeting.pongal_sankranti',
    palette: {
      bg: '#06101c',
      bgGlow: '#123a5c',
      surface: '#0c1a2a',
      felt: '#1b5e8c',
      feltDeep: '#103f61',
      feltLight: '#2a7cb3',
      accent: '#ffcc33',
      accentStrong: '#ffdb6e',
      cardBack: '#e4572e',
      cardBackPattern: '#ffcc33',
    },
    cardBack: 'kite',
    ambient: { kind: 'kites', intensity: 0.6 },
    celebration: 'confetti',
    emblem: 'kite',
  },
  raksha_bandhan: {
    id: 'raksha_bandhan',
    nameKey: 'theme.raksha_bandhan',
    greetingKey: 'greeting.raksha_bandhan',
    palette: {
      bg: '#12061a',
      bgGlow: '#441452',
      surface: '#1c0a26',
      felt: '#5e1f6e',
      feltDeep: '#3d1249',
      feltLight: '#7a2d8f',
      accent: '#ff7eb6',
      accentStrong: '#ffa3cb',
      accentText: '#2a0716',
      cardBack: '#f2a541',
      cardBackPattern: '#5e1f6e',
    },
    cardBack: 'rakhi',
    ambient: { kind: 'sparks', intensity: 0.5 },
    celebration: 'confetti',
    emblem: 'star',
  },
  independence_day: {
    id: 'independence_day',
    nameKey: 'theme.independence_day',
    greetingKey: 'greeting.independence_day',
    palette: {
      bg: '#050c18',
      bgGlow: '#13294d',
      surface: '#0a1626',
      felt: '#12376b',
      feltDeep: '#0b244a',
      feltLight: '#1c4f94',
      accent: '#ff9933',
      accentStrong: '#ffb366',
      teamA: '#3ddc84',
      cardBack: '#f7f3ea',
      cardBackPattern: '#000080',
    },
    cardBack: 'tricolor',
    ambient: { kind: 'confetti', intensity: 0.5 },
    celebration: 'tricolor',
    emblem: 'chakra',
  },
  republic_day: {
    id: 'republic_day',
    nameKey: 'theme.republic_day',
    greetingKey: 'greeting.republic_day',
    palette: {
      bg: '#050c18',
      bgGlow: '#13294d',
      surface: '#0a1626',
      felt: '#12376b',
      feltDeep: '#0b244a',
      feltLight: '#1c4f94',
      accent: '#ff9933',
      accentStrong: '#ffb366',
      teamA: '#3ddc84',
      cardBack: '#f7f3ea',
      cardBackPattern: '#000080',
    },
    cardBack: 'tricolor',
    ambient: { kind: 'confetti', intensity: 0.5 },
    celebration: 'tricolor',
    emblem: 'chakra',
  },
  eid: {
    id: 'eid',
    nameKey: 'theme.eid',
    greetingKey: 'greeting.eid',
    palette: {
      bg: '#04100f',
      bgGlow: '#0f3b36',
      surface: '#0a1c1a',
      felt: '#0f5048',
      feltDeep: '#0a3631',
      feltLight: '#17705f',
      accent: '#e8c766',
      accentStrong: '#f2da8f',
      cardBack: '#123c69',
      cardBackPattern: '#e8c766',
    },
    cardBack: 'crescent',
    ambient: { kind: 'lanterns', intensity: 0.6 },
    celebration: 'confetti',
    emblem: 'star',
  },
  christmas_newyear: {
    id: 'christmas_newyear',
    nameKey: 'theme.christmas_newyear',
    greetingKey: 'greeting.christmas_newyear',
    palette: {
      bg: '#050f0b',
      bgGlow: '#123b26',
      surface: '#0b1b14',
      felt: '#14533a',
      feltDeep: '#0c3826',
      feltLight: '#1e7350',
      accent: '#ef4f56',
      accentStrong: '#f47c81',
      accentText: '#2a0507',
      cardBack: '#b3202a',
      cardBackPattern: '#f7f3ea',
    },
    cardBack: 'snowflake',
    ambient: { kind: 'snow', intensity: 0.7 },
    celebration: 'snow',
    emblem: 'tree',
  },
};

export const THEMES: Readonly<Record<ThemeId, FestivalTheme>> = Object.fromEntries(
  Object.entries(SPECS).map(([id, spec]) => [
    id,
    { ...spec, palette: { ...ROYAL_PALETTE, ...spec.palette } },
  ]),
) as Record<ThemeId, FestivalTheme>;

const HEX_OR_RGBA = /^(#[0-9a-fA-F]{3,8}|rgba?\([\d\s.,%]+\))$/;

/**
 * Applies Remote Config overrides to a code-defined theme. Only known palette tokens with valid
 * colour values are accepted, so a console typo cannot break rendering.
 */
export function resolveTheme(id: ThemeId, overrides: ThemeOverrides = {}): FestivalTheme {
  const base = THEMES[id] ?? THEMES.default;
  const palette = { ...base.palette };
  for (const [token, value] of Object.entries(overrides.palette ?? {})) {
    if (token in palette && HEX_OR_RGBA.test(value)) palette[token as PaletteToken] = value;
  }
  const intensity =
    typeof overrides.ambientIntensity === 'number'
      ? Math.min(1, Math.max(0, overrides.ambientIntensity))
      : base.ambient.intensity;
  return { ...base, palette, ambient: { ...base.ambient, intensity } };
}

export function themeCssVars(theme: FestivalTheme): Record<string, string> {
  const vars: Record<string, string> = {};
  for (const [token, value] of Object.entries(theme.palette))
    vars[cssVarName(token as PaletteToken)] = value;
  return vars;
}
